import { isApiError, type ApiError } from "@sagot/api-client";
import { GRAPH_LAYER_VALUES, type GraphLayer } from "@sagot/constants";
import {
  GRAPH_ACTIVE_RUN_STATUSES,
  GRAPH_NAME_FORBIDDEN,
  GRAPH_NAME_MAX_CHARS,
  GRAPH_PAGE_SIZE,
  SECONDS_PER_MINUTE,
} from "./graph-constants";
import type { GraphBudget, GraphRun, GraphSummary, MergeRefusal } from "./graph-types";

/** "approved_by" -> "approved by": a relation kind or a type key as a person reads it. */
export function humanize(key: string): string {
  return key.replace(/[_-]+/g, " ").trim();
}

/** "program_office" -> "Program office": a type key as a chip label. */
export function typeLabel(key: string): string {
  const text = humanize(key);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function isRunActive(run: Pick<GraphRun, "status"> | null | undefined): boolean {
  return run !== null && run !== undefined && (GRAPH_ACTIVE_RUN_STATUSES as readonly string[]).includes(run.status);
}

/** Share of the month's allowance used (0 to 1), or `null` when the plan has none. */
export function budgetShare(budget: Pick<GraphBudget, "used" | "limit"> | null | undefined): number | null {
  if (!budget || budget.limit <= 0) return null;
  return Math.min(budget.used / budget.limit, 1);
}

/** Whole minutes to show for a wait in seconds ("in 5 min"); never 0 while there is still a wait. */
export function waitMinutes(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / SECONDS_PER_MINUTE));
}

export function pageCountOf(total: number, limit: number = GRAPH_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / limit));
}

/** "from" and "to" (1-based, inclusive) of the rows on a page, for "Showing 26 to 50 of 80". */
export function rowRange(page: number, shown: number, limit: number = GRAPH_PAGE_SIZE): { from: number; to: number } {
  if (shown <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * limit + 1;
  return { from, to: from + shown - 1 };
}

/** What stands between the owner and the graph, from the first layer that is off. */
export type OffCta = "knowledge" | "billing" | "agent";

export const OFF_CTA: Readonly<Record<GraphLayer, OffCta>> = {
  platform_flag: "knowledge",
  plan: "billing",
  tenant_override: "knowledge",
  agent: "agent",
};

export function isGraphLayer(value: unknown): value is GraphLayer {
  return typeof value === "string" && (GRAPH_LAYER_VALUES as readonly string[]).includes(value);
}

/** The layer a `409 graph_disabled` names (`details.layer`), or null for any other failure. */
export function layerOfError(error: unknown): GraphLayer | null {
  if (!isApiError(error) || error.code !== "graph_disabled") return null;
  const layer = error.details.layer;
  return isGraphLayer(layer) ? layer : null;
}

/** Why a merge was refused (`details.reason`), or null. */
export function mergeRefusalOf(error: ApiError): MergeRefusal | null {
  const reason = error.details.reason;
  return reason === "type_mismatch" || reason === "not_active" ? reason : null;
}

/** The first layer that is off for the page, or `null` when the graph can be read and curated. */
export function blockedLayer(summary: Pick<GraphSummary, "layers">): GraphLayer | null {
  return summary.layers.available ? null : summary.layers.blocked_by;
}

export type NameProblem = "empty" | "long" | "chars" | "same";

/** The checks the API makes on a typed name, so most mistakes are caught before a request goes out. */
export function nameProblem(raw: string, current: string | null = null): NameProblem | null {
  const text = raw.trim();
  if (!/[\p{L}\p{N}]/u.test(text)) return "empty";
  if (text.length > GRAPH_NAME_MAX_CHARS) return "long";
  if (GRAPH_NAME_FORBIDDEN.test(text)) return "chars";
  if (current !== null && text.toLowerCase() === current.trim().toLowerCase()) return "same";
  return null;
}

/** True when the graph holds nothing at all (every status), so the page can say "no graph yet". */
export function graphIsEmpty(summary: Pick<GraphSummary, "counts">): boolean {
  const counts = summary.counts;
  return counts === null || counts.entities + counts.hidden + counts.merged === 0;
}
